'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Tolerance, in CSS pixels, for every layout comparison in this hook.
 *
 * Browsers report scrollWidth/clientWidth/scrollLeft as fractional values under
 * zoom or fractional device-pixel ratios. Comparing them exactly leaves the
 * forward arrow enabled at the true end of the row (scrollLeft is 599.6 when max
 * is 600), so the user sees an arrow that does nothing. One pixel is below the
 * threshold of a scroll a user could perceive, and above the sub-pixel noise.
 */
const EPSILON = 1;

/** Fraction of the visible width traversed by one arrow press. */
const DEFAULT_STEP_RATIO = 0.8;

export interface UseScrollArrowsOptions {
  /** Fraction of the visible width moved per arrow press. Defaults to 0.8. */
  stepRatio?: number;
}

export interface UseScrollArrowsResult<T extends HTMLElement> {
  /** Attach to the horizontally scrolling element. */
  scrollRef: React.RefObject<T | null>;
  /** True when the content is wider than its box, i.e. arrows are warranted at all. */
  isOverflowing: boolean;
  /** False at the left edge. */
  canScrollLeft: boolean;
  /** False at the right edge. */
  canScrollRight: boolean;
  /** Scroll one step towards the start. No-ops at the left edge. */
  scrollPrev: () => void;
  /** Scroll one step towards the end. No-ops at the right edge. */
  scrollNext: () => void;
}

interface ScrollState {
  isOverflowing: boolean;
  canScrollLeft: boolean;
  canScrollRight: boolean;
}

const INITIAL_STATE: ScrollState = {
  isOverflowing: false,
  canScrollLeft: false,
  canScrollRight: false,
};

function isSameState(a: ScrollState, b: ScrollState): boolean {
  return (
    a.isOverflowing === b.isOverflowing &&
    a.canScrollLeft === b.canScrollLeft &&
    a.canScrollRight === b.canScrollRight
  );
}

/**
 * Track whether a horizontally scrolling element overflows, and drive prev/next
 * controls for it.
 *
 * Exists because a horizontally scrolling container is unreachable with a normal
 * mouse: a wheel emits vertical deltas only, and this app hides the horizontal
 * scrollbar at >=768px (globals.css). A trackpad's two-finger swipe emits
 * horizontal deltas and works; a mouse has no equivalent. Arrow buttons are the
 * affordance that closes that gap.
 *
 * Measurement runs on mount, on the element's own 'scroll', on window 'resize',
 * on ResizeObserver, on a webfont swap, and after every render. The render pass is
 * what makes the row re-evaluate when its content changes (options load in, a group
 * is joined) without the caller having to declare a dependency key; the font signal
 * covers the one content change that fires none of the others.
 */
export function useScrollArrows<T extends HTMLElement = HTMLDivElement>(
  options: UseScrollArrowsOptions = {},
): UseScrollArrowsResult<T> {
  const { stepRatio = DEFAULT_STEP_RATIO } = options;

  const scrollRef = useRef<T | null>(null);
  const [state, setState] = useState<ScrollState>(INITIAL_STATE);

  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
    const next: ScrollState = {
      isOverflowing: maxScrollLeft > EPSILON,
      canScrollLeft: el.scrollLeft > EPSILON,
      canScrollRight: el.scrollLeft < maxScrollLeft - EPSILON,
    };

    // Bail out when nothing changed. Required, not an optimisation: `measure` also
    // runs as a render effect below, so returning a fresh object every time would
    // schedule a state update on every render and loop forever.
    setState((prev) => (isSameState(prev, next) ? prev : next));
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    el.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);

    // Catches changes to the element's own box that the events above miss - a sibling
    // growing and squeezing this row, a container collapsing. It does NOT catch a font
    // swap; see below. Guarded because ResizeObserver is absent in some SSR/test runtimes.
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => measure()) : null;
    observer?.observe(el);

    // A webfont swap is the one content change nothing above sees. next/font is loaded
    // with `display: 'swap'` (layout.tsx), so the chips are first painted in fallback
    // glyphs and repainted in the real ones after mount. That changes their combined
    // width - the row's scrollWidth - while the scroller's own box does not move:
    // clientWidth is set by the parent, and the chips' `text-sm` pins an explicit
    // line-height, so the height is stable too. ResizeObserver watches the box, so it
    // stays silent; nothing scrolls; React does not re-render. The row can therefore
    // become overflowing with its arrows still unmounted - the very unreachable-chips
    // bug the arrows exist to fix, and one a mouse user cannot scroll their way out of.
    //
    // `ready` covers the initial swap; 'loadingdone' covers any font that loads later.
    // document.fonts is absent in jsdom and in SSR, hence the guard.
    //
    // The `ready` continuation needs no unmount latch of its own: React nulls the ref
    // on unmount, so a late resolution lands in `measure`'s existing empty-ref guard
    // and returns without touching state.
    const fonts = typeof document !== 'undefined' ? document.fonts : undefined;

    fonts?.ready.then(measure);
    fonts?.addEventListener('loadingdone', measure);

    return () => {
      el.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      observer?.disconnect();
      fonts?.removeEventListener('loadingdone', measure);
    };
  }, [measure]);

  // Re-measure after every render, so a changed child list (new option, removed
  // group) is picked up without the caller passing a dependency key. Safe only
  // because `measure` bails when the result is unchanged.
  useEffect(measure);

  const scrollByStep = useCallback(
    (direction: 1 | -1) => {
      const el = scrollRef.current;
      if (!el) {
        return;
      }

      const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
      const step = Math.max(1, el.clientWidth * stepRatio);
      const target = Math.min(maxScrollLeft, Math.max(0, el.scrollLeft + direction * step));

      // Already at that edge: do nothing at all, so the caller's aria-disabled
      // control is a genuine no-op rather than a scroll of zero pixels.
      if (Math.abs(target - el.scrollLeft) < EPSILON) {
        return;
      }

      if (typeof el.scrollTo === 'function') {
        el.scrollTo({ left: target, behavior: 'smooth' });
      } else {
        // jsdom, and any runtime without the scroll API, still needs the position
        // to move so that state stays truthful.
        el.scrollLeft = target;
      }

      measure();
    },
    [measure, stepRatio],
  );

  const scrollPrev = useCallback(() => scrollByStep(-1), [scrollByStep]);
  const scrollNext = useCallback(() => scrollByStep(1), [scrollByStep]);

  return {
    scrollRef,
    isOverflowing: state.isOverflowing,
    canScrollLeft: state.canScrollLeft,
    canScrollRight: state.canScrollRight,
    scrollPrev,
    scrollNext,
  };
}
